<template>
  <ol class="games-left" :style="{ '--slots': miniGames.length }">
    <li v-for="row in rows" :key="row.id" class="hand" :class="{ 'is-choosing': row.isChoosing }">
      <span class="who">
        <span v-if="row.isChoosing" class="choosing-dot" aria-hidden="true"></span>
        <span class="name">{{ row.name }}</span>
      </span>
      <span class="visually-hidden">
        {{ row.left.length ? `still to pick: ${row.left.join(', ')}` : 'has picked every game' }}
      </span>
      <span class="slots" aria-hidden="true">
        <span
          v-for="slot in row.slots"
          :key="slot.id"
          class="slot"
          :class="{ 'is-played': slot.round }"
          :title="slot.round ? `${slot.name}, round ${slot.round}` : slot.name"
        >
          <GameIndex :symbol="slot.symbol" size="sm" />
        </span>
      </span>
    </li>
  </ol>
</template>

<script setup>
import { computed } from 'vue';
import { useGameConfigStore } from '../stores/gameConfig';
import GameIndex from './GameIndex.vue';

const props = defineProps({
  players: {
    type: Array,
    required: true
  },
  rounds: {
    type: Array,
    required: true
  },
  miniGames: {
    type: Array,
    required: true
  },
  currentPlayerIndex: {
    type: Number,
    default: -1
  }
});

const gameConfig = useGameConfigStore();

const rows = computed(() =>
  props.players.map((player, i) => {
    const slots = props.miniGames.map((id) => {
      const game = gameConfig.games[id];
      const index = props.rounds.findIndex(
        (round) => round.miniGame.id === id && round.miniGame.selectedByPlayerId === player.id
      );
      return {
        id,
        name: game?.displayName ?? id,
        symbol: game?.symbol ?? '?',
        round: index === -1 ? null : index + 1
      };
    });
    return {
      id: player.id,
      name: player.name,
      isChoosing: i === props.currentPlayerIndex,
      slots,
      left: slots.filter((slot) => !slot.round).map((slot) => slot.name)
    };
  })
);
</script>

<style scoped>
.games-left {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.hand {
  display: grid;
  grid-template-columns: minmax(4rem, 7.5rem) minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  min-height: 2.5rem;
}

.who {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--on-table-muted);
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9375rem;
  font-weight: 500;
}

.is-choosing .name {
  color: var(--on-table);
  font-weight: 600;
}

.choosing-dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: var(--focus);
}

.slots {
  display: grid;
  grid-template-columns: repeat(var(--slots), minmax(1.625rem, 3rem));
  justify-content: space-between;
  gap: 0.25rem;
}

.slot {
  display: flex;
  justify-content: center;
}

.is-played :deep(.game-index) {
  background-color: transparent;
  border: 1px dashed var(--table-line);
  color: rgba(154, 163, 181, 0.5);
}

@media (max-width: 419px) {
  .hand {
    grid-template-columns: minmax(3.5rem, 1fr) auto;
  }

  .slots {
    grid-template-columns: repeat(var(--slots), 1.5rem);
  }

  .slot :deep(.game-index) {
    width: 1.5rem;
    height: 2rem;
    font-size: 0.875rem;
  }
}
</style>
